import { v4 as uuidv4 } from 'uuid';

import type { Embed } from '..';
import type { VideoEventDetail } from '@vouchfor/media-player';
import type { ReactiveController, ReactiveControllerHost } from 'lit';

import packageJson from '../../../../package.json';
import { getEnvUrls } from '~/utils/env';

const MINIMUM_SEND_THRESHOLD = 1;

type EmbedHost = ReactiveControllerHost & Embed;

type TrackingEvent = 'VOUCH_LOADED' | 'VOUCH_RESPONSE_VIEWED' | 'VIDEO_PLAYED' | 'VIDEO_STREAMED';
type TrackingPayload = {
  vouchId?: string;
  answerId?: string;
  streamStart?: number;
  streamEnd?: number;
};

type TimeMap = {
  [key: string]: number;
};

type BooleanMap = {
  [key: string]: boolean;
};

class TrackingController implements ReactiveController {
  host: EmbedHost;

  private _tabId: string | undefined = undefined;
  private _clientId: string | undefined = undefined;
  private _visitorId: string | undefined = undefined;

  private _hasPlayed = false;
  private _hasLoaded: BooleanMap = {};
  private _answersViewed: BooleanMap = {};
  private _streamStartTime: TimeMap = {};
  private _streamLatestTime: TimeMap = {};
  private _currentlyPlayingVideo: VideoEventDetail | null = null;

  constructor(host: EmbedHost) {
    this.host = host;
    host.addController(this);
  }

  private _findVouchId(payload?: TrackingPayload) {
    if (payload && 'vouchId' in payload) {
      return payload.vouchId;
    }
    if (this.host.vouch) {
      return this.host.vouch.id;
    }
    return null;
  }

  private _createVisitor = (visitorId: string) => {
    const { publicApiUrl } = getEnvUrls(this.host.env);
    window.localStorage?.setItem?.('vouch-uid-visitor', visitorId);
    navigator.sendBeacon(`${publicApiUrl}/api/visitor`, JSON.stringify({ visitorId }));
    return visitorId;
  };

  private _getUids() {
    if (typeof window === 'undefined') {
      return {
        client: null,
        tab: null,
        request: uuidv4()
      };
    }

    // Persisted for a user for the same device + browser, so we can e.g. search for all logs related to that browser
    const visitorId =
      this._visitorId || window.localStorage?.getItem?.('vouch-uid-visitor') || this._createVisitor(uuidv4());
    // Persisted for a user for the same device + browser, so we can e.g. search for all logs related to that browser
    const clientId = this._clientId || window.localStorage?.getItem?.('vouch-uid-client') || uuidv4();
    // Persisted in session storage, so we can search for everything the user has done in a specific tab
    const tabId = this._tabId || window.sessionStorage?.getItem?.('vouch-uid-tab') || uuidv4();
    // Not persisted, allows us to search for any logs related to a single FE request
    // E.g. BE should pass this request ID through all other services to be able to group logs
    const requestId = uuidv4();

    // Cache and persist uids
    if (visitorId !== this._visitorId) {
      this._visitorId = visitorId;
      window.localStorage?.setItem?.('vouch-uid-visitor', visitorId);
    }

    if (clientId !== this._clientId) {
      this._clientId = clientId;
      window.localStorage?.setItem?.('vouch-uid-client', clientId);
    }

    if (tabId !== this._tabId) {
      this._tabId = tabId;
      window.sessionStorage?.setItem?.('vouch-uid-tab', tabId);
    }

    return {
      client: clientId,
      tab: tabId,
      request: requestId,
      visitor: visitorId
    };
  }

  private _getReportingMetadata = () => {
    const [country, region] = Intl.DateTimeFormat().resolvedOptions().timeZone?.split?.('/') ?? [];

    const utmParams: any = {};
    [...new URLSearchParams(location.search).entries()].forEach(([key, value]) => {
      if (/utm/.test(key)) {
        const param = key.toLowerCase().replace(/[-_][a-z0-9]/g, (group) => group.slice(-1).toUpperCase());
        utmParams[param] = value;
      }
    });

    return {
      source: this.host.trackingSource,
      time: new Date(),
      region,
      country,
      screenHeight: window.screen.height,
      screenWidth: window.screen.width,
      referrer: document.referrer,
      currentUrl: location.href,
      ...utmParams
    };
  };

  private _sendTrackingEvent = (event: TrackingEvent, payload?: TrackingPayload) => {
    const vouchId = this._findVouchId(payload);

    if (!vouchId || this.host.disableTracking) {
      return;
    }

    const { publicApiUrl } = getEnvUrls(this.host.env);
    const { client, tab, request, visitor } = this._getUids();

    navigator.sendBeacon(
      `${publicApiUrl}/api/v2/events`,
      JSON.stringify({
        event,
        payload: {
          ...payload,
          vouchId
        },
        context: {
          'x-uid-client': client,
          'x-uid-tab': tab,
          'x-uid-request': request,
          'x-uid-visitor': visitor,
          'x-reporting-metadata': this._getReportingMetadata(),
          'x-embeds-version': packageJson.version
        }
      })
    );
  };

  private _streamEnded = () => {
    if (this._currentlyPlayingVideo) {
      const { id, key } = this._currentlyPlayingVideo;
      // Don't send a tracking event when seeking backwards
      if (this._streamLatestTime[key] > this._streamStartTime[key] + MINIMUM_SEND_THRESHOLD) {
        // Send a video streamed event any time the stream ends to capture the time between starting
        // the video and the video stopping for any reason (pausing, deleting the embed node or closing the browser)
        this._sendTrackingEvent('VIDEO_STREAMED', {
          answerId: id,
          streamStart: this._streamStartTime[key],
          streamEnd: this._streamLatestTime[key]
        });
      }

      // Make sure these events are only sent once by deleting the start and latest times
      delete this._streamStartTime[key];
      delete this._streamLatestTime[key];
    }
  };

  private _handleVouchLoaded = ({ detail: vouchId }: CustomEvent<string>) => {
    if (!vouchId) {
      return;
    }

    // Only send loaded event once per session
    if (!this._hasLoaded[vouchId]) {
      this._sendTrackingEvent('VOUCH_LOADED', { vouchId });
      this._hasLoaded[vouchId] = true;
    }
  };

  private _handlePlay = () => {
    // Only send the video played event once per session
    if (!this._hasPlayed) {
      this._sendTrackingEvent('VIDEO_PLAYED', {
        streamStart: this.host.currentTime
      });
      this._hasPlayed = true;
    }
  };

  private _handleVideoPlay = ({ detail: { id, key, node } }: CustomEvent<VideoEventDetail>) => {
    // Only increment play count once per session
    if (!this._answersViewed[key]) {
      this._sendTrackingEvent('VOUCH_RESPONSE_VIEWED', {
        answerId: id
      });
      this._answersViewed[key] = true;
    }

    if (!this._streamStartTime[key]) {
      this._streamStartTime[key] = node.currentTime;
      this._streamLatestTime[key] = node.currentTime;
    }
  };

  private _handleVideoTimeUpdate = ({ detail: { id, key, node } }: CustomEvent<VideoEventDetail>) => {
    if (
      // We only want to count any time that the video is actually playing
      !this.host.paused &&
      // Only update the latest time if this event fires for the currently active video
      id === this.host.scene?.video?.id
    ) {
      this._currentlyPlayingVideo = { id, key, node };
      this._streamLatestTime[key] = node.currentTime;
    }
  };

  private _handleVideoPause = ({ detail: { id, key } }: CustomEvent<VideoEventDetail>) => {
    if (this._streamLatestTime[key] > this._streamStartTime[key] + MINIMUM_SEND_THRESHOLD) {
      this._sendTrackingEvent('VIDEO_STREAMED', {
        answerId: id,
        streamStart: this._streamStartTime[key],
        streamEnd: this._streamLatestTime[key]
      });
    }
    delete this._streamStartTime[key];
    delete this._streamLatestTime[key];
  };

  private _pageUnloading = () => {
    this._streamEnded();
    // This will try to send the same stream event again so we delete the start and latest
    // time in stream ended so that there is no times to send and the pause event does nothing
    this.host.pause();
  };

  private _handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      this._pageUnloading();
    }
  };

  private _handlePageHide = () => {
    this._pageUnloading();
  };

  hostConnected() {
    requestAnimationFrame(() => {
      if ('onvisibilitychange' in document) {
        document.addEventListener('visibilitychange', this._handleVisibilityChange);
      } else {
        window.addEventListener('pagehide', this._handlePageHide);
      }
      this.host.addEventListener('vouch:loaded', this._handleVouchLoaded);
      this.host.mediaPlayer?.addEventListener('play', this._handlePlay);
      this.host.mediaPlayer?.addEventListener('video:play', this._handleVideoPlay);
      this.host.mediaPlayer?.addEventListener('video:pause', this._handleVideoPause);
      this.host.mediaPlayer?.addEventListener('video:timeupdate', this._handleVideoTimeUpdate);
    });
  }

  hostDisconnected() {
    this._streamEnded();
    if ('onvisibilitychange' in document) {
      document.removeEventListener('visibilitychange', this._handleVisibilityChange);
    } else {
      window.removeEventListener('pagehide', this._handlePageHide);
    }
    this.host.removeEventListener('vouch:loaded', this._handleVouchLoaded);
    this.host.mediaPlayer?.removeEventListener('play', this._handlePlay);
    this.host.mediaPlayer?.removeEventListener('video:play', this._handleVideoPlay);
    this.host.mediaPlayer?.removeEventListener('video:pause', this._handleVideoPause);
    this.host.mediaPlayer?.removeEventListener('video:timeupdate', this._handleVideoTimeUpdate);
  }
}

export { TrackingController };
